(function () {
  'use strict';

  var OPEN_ITEM = 'cwc_header__item--open';
  var DISMISSED_ITEM = 'cwc_header__item--dismissed';
  var DRAWER_OPEN = 'cwc_header__drawer--open';
  var SCRIM_VISIBLE = 'cwc_header__scrim--visible';
  var ACC_OPEN = 'cwc_header__acc--open';
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])';

  /* ==============================
     Desktop mega menu

     Hover and focus-within open the panel in CSS alone, so the menu works
     before this file has parsed and keeps working if it never does. What is
     left here is the part CSS cannot express: reflecting the open state to
     assistive tech, and closing on Escape.
     ============================== */
  function initMega(sectionEl) {
    var items = sectionEl.querySelectorAll('[data-cwc-nav-item]');
    if (!items.length) return;

    function setOpen(item, isOpen) {
      var trigger = item.querySelector('[data-cwc-nav-trigger]');
      var panel = item.querySelector('[data-cwc-mega]');

      item.classList.toggle(OPEN_ITEM, isOpen);
      if (trigger) trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

      /**
       * The attribute is kept in step for assistive tech, but CSS overrides the
       * display it would normally impose — the panel animates, and display:none
       * cannot be transitioned. Visibility in the stylesheet is what actually
       * takes it out of the tab order.
       */
      if (panel) panel.hidden = !isOpen;
    }

    function closeAll() {
      items.forEach(function (item) {
        setOpen(item, false);
      });
    }

    function closeOthers(keep) {
      items.forEach(function (item) {
        if (item !== keep) setOpen(item, false);
      });
    }

    /**
     * Closing is immediate.
     *
     * An earlier version deferred it, to survive the pointer crossing a gap
     * between a trigger and its panel. There is no gap: items stretch to the
     * bar's full height and the panel's top:100% resolves against the bar's
     * padding box, which excludes its border — the two edges meet exactly.
     *
     * Deferring it was actively harmful. All panels share one strip under the
     * bar, so holding the outgoing one open while the next was arriving painted
     * the old panel over the new one for as long as the delay lasted.
     */
    items.forEach(function (item) {
      var panel = item.querySelector('[data-cwc-mega]');
      if (!panel) return;

      // Hover and focus are the CSS-driven states; mirror them onto the
      // attributes rather than driving the visuals from here.
      item.addEventListener('mouseenter', function () {
        // Any other panel gives way at once — two open at the same time is the
        // one state this strip cannot render.
        item.classList.remove(DISMISSED_ITEM);
        closeOthers(item);
        setOpen(item, true);
      });
      item.addEventListener('mouseleave', function () {
        item.classList.remove(DISMISSED_ITEM);
        setOpen(item, false);
      });
      item.addEventListener('focusin', function () {
        // Escape hands focus back to this item's trigger; that focus must not
        // reopen the panel it has just closed.
        if (item.classList.contains(DISMISSED_ITEM)) return;
        closeOthers(item);
        setOpen(item, true);
      });
      item.addEventListener('focusout', function (event) {
        // focusout fires before the new element has focus, so a move between
        // two links inside the same panel would otherwise close it.
        if (item.contains(event.relatedTarget)) return;
        item.classList.remove(DISMISSED_ITEM);
        setOpen(item, false);
      });
    });

    /**
     * On the document, not the section: a panel opened by hover leaves focus
     * wherever it was, usually outside the header, and Escape has to close it
     * from there too.
     */
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;

      var open = sectionEl.querySelector('.' + OPEN_ITEM);
      if (!open) return;

      var focusInside = open.contains(document.activeElement);
      closeAll();

      /**
       * Held shut until the pointer or focus leaves the item. Without it the
       * trigger taking focus below reopens the panel through focusin and
       * :focus-within, and a pointer still resting on the item through :hover.
       * The stylesheet gives this class the last word over both.
       */
      open.classList.add(DISMISSED_ITEM);

      // Focus inside the panel goes back to the trigger, or Escape would leave
      // the caret somewhere that is no longer visible. Focus elsewhere stays put.
      if (!focusInside) return;
      var trigger = open.querySelector('[data-cwc-nav-trigger]');
      if (trigger) trigger.focus();
    });
  }

  /* ==============================
     Mobile drawer
     ============================== */
  function initDrawer(sectionEl) {
    var toggle = sectionEl.querySelector('[data-cwc-toggle]');
    var drawer = sectionEl.querySelector('[data-cwc-drawer]');
    if (!toggle || !drawer) return;

    var scrim = sectionEl.querySelector('[data-cwc-scrim]');
    var closeButton = drawer.querySelector('[data-cwc-drawer-close]');

    // Focusable itself, the fallback when a drawer has no close button: opening
    // still moves focus inside without landing on the search field, which would
    // raise a phone's keyboard.
    if (!drawer.hasAttribute('tabindex')) drawer.setAttribute('tabindex', '-1');

    function isOpen() {
      return drawer.classList.contains(DRAWER_OPEN);
    }

    function open() {
      // Unhide first, then add the class on the next frame: an element that was
      // display:none has no start value for the transform to animate from.
      drawer.hidden = false;
      if (scrim) scrim.hidden = false;

      requestAnimationFrame(function () {
        drawer.classList.add(DRAWER_OPEN);
        if (scrim) scrim.classList.add(SCRIM_VISIBLE);

        // Focus lands on the close button. One more frame first: the drawer is
        // still visibility:hidden on the frame the class lands, and focus() on
        // a hidden element is ignored.
        requestAnimationFrame(function () {
          if (isOpen() && !drawer.contains(document.activeElement)) (closeButton || drawer).focus({ preventScroll: true });
        });
      });

      drawer.setAttribute('aria-hidden', 'false');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', 'Close menu');
      document.body.style.overflow = 'hidden';
    }

    function close() {
      if (!isOpen()) return;

      drawer.classList.remove(DRAWER_OPEN);
      if (scrim) scrim.classList.remove(SCRIM_VISIBLE);

      drawer.setAttribute('aria-hidden', 'true');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Open menu');
      document.body.style.overflow = '';

      toggle.focus();
    }

    toggle.addEventListener('click', function () {
      if (isOpen()) {
        close();
      } else {
        open();
      }
    });

    if (closeButton) closeButton.addEventListener('click', close);
    if (scrim) scrim.addEventListener('click', close);

    /**
     * Every other link in here navigates, and the page unload is what takes this
     * drawer down. The cart link does not: it carries aria-controls, so the
     * theme's cart drawer opens over the top and nothing unloads. Closing on the
     * click hands the viewport over instead of stacking two overlays.
     *
     * Bound to the element, so it runs in the target phase — ahead of the theme's
     * delegated listener on document.body, which is what opens the cart drawer.
     */
    var cartLink = drawer.querySelector('.cwc_header__drawer-cart[aria-controls]');
    if (cartLink) cartLink.addEventListener('click', close);

    document.addEventListener('keydown', function (event) {
      if (!isOpen()) return;
      if (event.key === 'Escape') close();
      else if (event.key === 'Tab') keepFocusInside(event);
    });

    /**
     * Tab and Shift+Tab cycle inside the open drawer. The page behind it is
     * covered by the scrim, so a focus that wandered out would sit on links
     * nobody can see.
     */
    function keepFocusInside(event) {
      var focusables = Array.prototype.filter.call(drawer.querySelectorAll(FOCUSABLE), function (el) {
        return el.getClientRects().length > 0;
      });
      if (!focusables.length) return;

      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      var active = document.activeElement;
      var leaving = !drawer.contains(active) ||
        (event.shiftKey ? active === first || active === drawer : active === last);

      if (leaving) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    }

    /**
     * A drawer left open behind a widening viewport would sit over a nav bar
     * that has already come back, with no visible control to close it.
     */
    window.addEventListener('resize', function () {
      if (isOpen() && !isCollapsed(sectionEl)) close();
    });

    /* ---- accordions ---- */
    drawer.querySelectorAll('[data-cwc-acc-toggle]').forEach(function (accToggle) {
      accToggle.addEventListener('click', function () {
        var acc = accToggle.closest('[data-cwc-acc]');
        if (!acc) return;

        var panel = acc.querySelector('[data-cwc-acc-panel]');
        var willOpen = !acc.classList.contains(ACC_OPEN);

        acc.classList.toggle(ACC_OPEN, willOpen);
        accToggle.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        if (panel) slidePanel(panel, willOpen);

        var sign = accToggle.querySelector('.cwc_header__acc-sign');
        if (sign) sign.textContent = willOpen ? '\u2212' : '+';
      });
    });
  }

  /**
   * Opens or closes a drawer accordion panel with a height ease, the same
   * motion as the page accordions (Arsalan 2026-10-21: accordions and
   * dropdowns should move smoothly). [hidden] stays the source of truth:
   * a close sets it when the ease ends. The height is measured before a
   * running ease is cancelled, so a second tap mid-way turns it around.
   * Reduced motion keeps the instant toggle.
   */
  function slidePanel(panel, open) {
    var from = panel.hidden ? 0 : panel.getBoundingClientRect().height;
    if (panel.cwcSlide) {
      panel.cwcSlide.cancel();
      panel.cwcSlide = null;
    }
    var calm = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (calm || !panel.animate) {
      panel.hidden = !open;
      return;
    }

    panel.hidden = false;
    var to = open ? panel.getBoundingClientRect().height : 0;
    var pad = window.getComputedStyle(panel).paddingBottom;
    panel.style.overflow = 'hidden';
    panel.style.boxSizing = 'border-box';
    var slide = panel.animate(
      open
        ? { height: [from + 'px', to + 'px'], paddingBottom: [from ? pad : '0px', pad], opacity: [from ? 1 : 0, 1] }
        : { height: [from + 'px', '0px'], paddingBottom: [pad, '0px'], opacity: [1, 0] },
      { duration: open ? 320 : 240, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' }
    );
    panel.cwcSlide = slide;
    slide.onfinish = function () {
      panel.cwcSlide = null;
      panel.style.overflow = '';
      panel.style.boxSizing = '';
      if (!open) panel.hidden = true;
    };
  }

  /**
   * Whether the nav has collapsed to the burger.
   *
   * The stylesheet carries a max-width query at the schema default, which is
   * what makes the collapse work before this file runs. A merchant who moves
   * the breakpoint cannot rewrite that query, so the class below overrides it —
   * and is only applied when the two would actually disagree, so the common
   * case stays pure CSS.
   */
  function isCollapsed(sectionEl) {
    var breakpoint = parseInt(sectionEl.getAttribute('data-cwc-breakpoint'), 10) || 1000;
    return window.innerWidth <= breakpoint;
  }

  function initBreakpoint(sectionEl) {
    var breakpoint = parseInt(sectionEl.getAttribute('data-cwc-breakpoint'), 10) || 1000;
    if (breakpoint === 1000) return;

    function sync() {
      var collapsed = isCollapsed(sectionEl);
      sectionEl.classList.toggle('cwc_header--collapsed', collapsed);
      sectionEl.classList.toggle('cwc_header--expanded', !collapsed);
    }

    window.addEventListener('resize', sync);
    sync();
  }

  /* ==============================
     Predictive search

     Owned by the theme, not by this file. The panel is a <header-search>
     DialogElement opened by the trigger's aria-controls, and <predictive-search>
     does the live-as-you-type fetching. Both bind themselves on upgrade, so
     there is nothing to initialise here — see snippets/CWC_header-search.liquid.
     ============================== */

  function initSection(sectionEl) {
    /**
     * Once per element. es-module-shims, the theme's import-map polyfill, fires
     * a second, synthetic DOMContentLoaded on the browsers it loads for, and
     * binding again on it gave every drawer accordion two click handlers: one
     * tap opened and shut it in the same moment. A section the theme editor
     * re-renders is a new element, so it still binds.
     */
    if (!sectionEl || sectionEl.hasAttribute('data-cwc-bound')) return;
    sectionEl.setAttribute('data-cwc-bound', '');

    // independent, so a header without a menu still gets a drawer and vice versa
    initMega(sectionEl);
    initDrawer(sectionEl);
    initBreakpoint(sectionEl);
  }

  function initAllSections() {
    document.querySelectorAll('.cwc_header').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllSections);
  } else {
    initAllSections();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('.cwc_header');
    if (section) initSection(section);
  });
})();
